import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  PLAYLIST_DISPLAY_HUB,
  PlaylistDisplayHubResponseType,
  PlaylistDisplayHubsResponseType,
  PlaylistDisplayHubStateType,
  PlaylistDisplayHubType,
} from './types';
import { updatePlaylistDisplayHubs } from './utils';

const playlistDisplayHubInitialState: PlaylistDisplayHubStateType = {
  playlistDisplayHubs: null,
  selectedPlaylistDisplayHub: null,
  loading: true,
  error: null,
};

export const playlistDisplayHubSlice = createSlice({
  name: PLAYLIST_DISPLAY_HUB,
  initialState: playlistDisplayHubInitialState,
  reducers: {
    // CREATE
    requestCreatePlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveCreatePlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.loading = false;
    },
    failedCreatePlaylistDisplayHub: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },
    // DELETE
    requestDeletePlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveDeletePlaylistDisplayHub: (
      playlistDisplayHub,
      { payload: playlistDisplayHubId }: PayloadAction<string>
    ) => {
      if (playlistDisplayHubId === playlistDisplayHub.selectedPlaylistDisplayHub?.id) {
        playlistDisplayHub.selectedPlaylistDisplayHub = null;
      }
      playlistDisplayHub.loading = false;
    },
    failedDeletePlaylistDisplayHub: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },

    // DUPLICATE
    requestDuplicatePlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveDuplicatePlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.loading = false;
    },
    failedDuplicatePlaylistDisplayHub: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },

    // UPDATE
    requestUpdatePlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveUpdatePlaylistDisplayHub: (
      playlistDisplayHub,
      { payload }: PayloadAction<PlaylistDisplayHubType>
    ) => {
      playlistDisplayHub.loading = false;

      if (!playlistDisplayHub.playlistDisplayHubs) return;
      //Insert updated playlistDisplayHub into playlistDisplayHubs array
      playlistDisplayHub.playlistDisplayHubs = playlistDisplayHub.playlistDisplayHubs?.map(
        (displayHub) => (displayHub.id === payload.id ? payload : displayHub)
      );
      //Insert updated playlistDisplayHub into selectedPlaylistDisplayHub
      if (playlistDisplayHub?.selectedPlaylistDisplayHub?.id === payload.id) {
        playlistDisplayHub.selectedPlaylistDisplayHub = payload;
      }
    },
    failedUpdatePlaylistDisplayHub: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },
    // GET ALL
    requestGetPlaylistDisplayHubs: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveGetPlaylistDisplayHubs: (
      playlistDisplayHub,
      { payload }: PayloadAction<PlaylistDisplayHubsResponseType | null>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.playlistDisplayHubs = payload;

      if (payload) {
        playlistDisplayHub.selectedPlaylistDisplayHub =
          payload.find(
            (displayHub) => displayHub.id === playlistDisplayHub.selectedPlaylistDisplayHub?.id
          ) || null;
      }
    },
    failedGetPlaylistDisplayHubs: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },
    // SET SELECTED
    requestSetSelectedPlaylistDisplayHub: (playlistDisplayHub) => {
      playlistDisplayHub.error = null;
    },
    receiveSetSelectedPlaylistDisplayHub: (
      playlistDisplayHub,
      { payload }: PayloadAction<PlaylistDisplayHubResponseType | null>
    ) => {
      playlistDisplayHub.selectedPlaylistDisplayHub = payload;
    },
    failedSetSelectedPlaylistDisplayHub: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.error = error;
    },
    // REORDER
    requestReorderPlaylistDisplayHubs: (playlistDisplayHub) => {
      playlistDisplayHub.error = null;
    },
    receiveReorderPlaylistDisplayHubs: (
      playlistDisplayHub,
      { payload: playlistDisplayHubIds }: PayloadAction<string[]>
    ) => {
      if (!playlistDisplayHub.playlistDisplayHubs) return;
      playlistDisplayHub.playlistDisplayHubs.sort((a, b) => {
        const indexA = playlistDisplayHubIds.indexOf(a.id);
        const indexB = playlistDisplayHubIds.indexOf(b.id);
        if (indexA < indexB) {
          return -1;
        }
        if (indexA > indexB) {
          return 1;
        }
        return 0;
      });
    },
    failedReorderPlaylistDisplayHubs: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.error = error;
    },
    // UPDATE MULTIPLE
    requestUpdatePlaylistDisplayHubs: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveUpdatePlaylistDisplayHubs: (
      playlistDisplayHub,
      { payload }: PayloadAction<PlaylistDisplayHubsResponseType>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.playlistDisplayHubs = updatePlaylistDisplayHubs(
        payload,
        playlistDisplayHub.playlistDisplayHubs
      );

      if (playlistDisplayHub.selectedPlaylistDisplayHub) {
        const selectedPlaylistDisplayHub = payload.find(
          (displayHub) => displayHub.id === playlistDisplayHub.selectedPlaylistDisplayHub?.id
        );
        if (selectedPlaylistDisplayHub) {
          playlistDisplayHub.selectedPlaylistDisplayHub = selectedPlaylistDisplayHub;
        }
      }
    },
    failedUpdatePlaylistDisplayHubs: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },
    // UPDATE PREVIEW
    requestUpdatePlaylistDisplayHubPreview: (playlistDisplayHub) => {
      playlistDisplayHub.loading = true;
    },
    receiveUpdatePlaylistDisplayHubPreview: (playlistDisplayHub) => {
      playlistDisplayHub.loading = false;
    },
    failedUpdatePlaylistDisplayHubPreview: (
      playlistDisplayHub,
      { payload: error }: PayloadAction<string>
    ) => {
      playlistDisplayHub.loading = false;
      playlistDisplayHub.error = error;
    },
  },
});

const actions = playlistDisplayHubSlice.actions;
const playlistDisplayHubReducer = playlistDisplayHubSlice.reducer;

export { actions, playlistDisplayHubReducer };
